/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  RemoveUserErpCredentialsPathParameters,
  RetrieveUserErpCredentialsParams,
  TLCredentialsListResponse,
  TLHTTPValidationError,
  TLSalesforceCredentialsRequest,
  TLSalesforceCredentialsResponse,
  TLSAPCredentialsRequest,
  TLSAPCredentialsResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Get all ERP credentials for the authenticated user
 * @summary Retrieve User ERP Credentials
 */
export const retrieveUserErpCredentials = (
  params?: RetrieveUserErpCredentialsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCredentialsListResponse>({ url: `/user-credentials`, method: 'GET', params, signal }, options);
};

export const getRetrieveUserErpCredentialsQueryKey = (params?: RetrieveUserErpCredentialsParams) => {
  return ['user-credentials', ...(params ? [params] : [])] as const;
};

export const getRetrieveUserErpCredentialsQueryOptions = <
  TData = Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: RetrieveUserErpCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retrieveUserErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRetrieveUserErpCredentialsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof retrieveUserErpCredentials>>> = ({ signal }) =>
    retrieveUserErpCredentials(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RetrieveUserErpCredentialsQueryResult = NonNullable<Awaited<ReturnType<typeof retrieveUserErpCredentials>>>;
export type RetrieveUserErpCredentialsQueryError = ErrorType<TLHTTPValidationError>;

export function useRetrieveUserErpCredentials<
  TData = Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | RetrieveUserErpCredentialsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof retrieveUserErpCredentials>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
          TError,
          Awaited<ReturnType<typeof retrieveUserErpCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRetrieveUserErpCredentials<
  TData = Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: RetrieveUserErpCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retrieveUserErpCredentials>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
          TError,
          Awaited<ReturnType<typeof retrieveUserErpCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRetrieveUserErpCredentials<
  TData = Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: RetrieveUserErpCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retrieveUserErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve User ERP Credentials
 */

export function useRetrieveUserErpCredentials<
  TData = Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: RetrieveUserErpCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retrieveUserErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRetrieveUserErpCredentialsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Retrieve User ERP Credentials
 */
export const prefetchRetrieveUserErpCredentialsQuery = async <
  TData = Awaited<ReturnType<typeof retrieveUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: RetrieveUserErpCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retrieveUserErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRetrieveUserErpCredentialsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Retrieve User ERP Credentials
 */
export const invalidateRetrieveUserErpCredentials = async (
  queryClient: QueryClient,
  params?: RetrieveUserErpCredentialsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRetrieveUserErpCredentialsQueryKey(params) }, options);

  return queryClient;
};

/**
 * Remove ERP credentials by ID for the authenticated user
 * @summary Remove User ERP Credentials
 */
export const removeUserErpCredentials = (
  { credentialId }: RemoveUserErpCredentialsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/user-credentials/delete/${credentialId}`, method: 'DELETE', signal }, options);
};

export const getRemoveUserErpCredentialsQueryKey = ({ credentialId }: RemoveUserErpCredentialsPathParameters) => {
  return ['DELETE', 'user-credentials', 'delete', credentialId] as const;
};

export const getRemoveUserErpCredentialsQueryOptions = <
  TData = Awaited<ReturnType<typeof removeUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: RemoveUserErpCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeUserErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRemoveUserErpCredentialsQueryKey({ credentialId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof removeUserErpCredentials>>> = ({ signal }) =>
    removeUserErpCredentials({ credentialId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof removeUserErpCredentials>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RemoveUserErpCredentialsQueryResult = NonNullable<Awaited<ReturnType<typeof removeUserErpCredentials>>>;
export type RemoveUserErpCredentialsQueryError = ErrorType<TLHTTPValidationError>;

export function useRemoveUserErpCredentials<
  TData = Awaited<ReturnType<typeof removeUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveUserErpCredentialsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeUserErpCredentials>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeUserErpCredentials>>,
          TError,
          Awaited<ReturnType<typeof removeUserErpCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveUserErpCredentials<
  TData = Awaited<ReturnType<typeof removeUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveUserErpCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeUserErpCredentials>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeUserErpCredentials>>,
          TError,
          Awaited<ReturnType<typeof removeUserErpCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveUserErpCredentials<
  TData = Awaited<ReturnType<typeof removeUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveUserErpCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeUserErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove User ERP Credentials
 */

export function useRemoveUserErpCredentials<
  TData = Awaited<ReturnType<typeof removeUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: RemoveUserErpCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeUserErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemoveUserErpCredentialsQueryOptions({ credentialId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remove User ERP Credentials
 */
export const prefetchRemoveUserErpCredentialsQuery = async <
  TData = Awaited<ReturnType<typeof removeUserErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: RemoveUserErpCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeUserErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemoveUserErpCredentialsQueryOptions({ credentialId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remove User ERP Credentials
 */
export const invalidateRemoveUserErpCredentials = async (
  queryClient: QueryClient,
  { credentialId }: RemoveUserErpCredentialsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRemoveUserErpCredentialsQueryKey({ credentialId }) }, options);

  return queryClient;
};

/**
 * Create new Salesforce CRM credentials for the authenticated user
 * @summary Create Salesforce CRM Credentials
 */
export const createSalesforceCrmCredentials = (
  tLSalesforceCredentialsRequest: BodyType<TLSalesforceCredentialsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSalesforceCredentialsResponse>(
    {
      url: `/user-credentials/salesforce`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSalesforceCredentialsRequest,
      signal,
    },
    options,
  );
};

export const getCreateSalesforceCrmCredentialsQueryKey = (
  tLSalesforceCredentialsRequest?: BodyType<TLSalesforceCredentialsRequest>,
) => {
  return ['POST', 'user-credentials', 'salesforce', tLSalesforceCredentialsRequest] as const;
};

export const getCreateSalesforceCrmCredentialsQueryOptions = <
  TData = Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSalesforceCredentialsRequest: BodyType<TLSalesforceCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSalesforceCrmCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateSalesforceCrmCredentialsQueryKey(tLSalesforceCredentialsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createSalesforceCrmCredentials>>> = ({ signal }) =>
    createSalesforceCrmCredentials(tLSalesforceCredentialsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateSalesforceCrmCredentialsQueryResult = NonNullable<
  Awaited<ReturnType<typeof createSalesforceCrmCredentials>>
>;
export type CreateSalesforceCrmCredentialsQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateSalesforceCrmCredentials<
  TData = Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSalesforceCredentialsRequest: BodyType<TLSalesforceCredentialsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSalesforceCrmCredentials>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
          TError,
          Awaited<ReturnType<typeof createSalesforceCrmCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateSalesforceCrmCredentials<
  TData = Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSalesforceCredentialsRequest: BodyType<TLSalesforceCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSalesforceCrmCredentials>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
          TError,
          Awaited<ReturnType<typeof createSalesforceCrmCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateSalesforceCrmCredentials<
  TData = Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSalesforceCredentialsRequest: BodyType<TLSalesforceCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSalesforceCrmCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Salesforce CRM Credentials
 */

export function useCreateSalesforceCrmCredentials<
  TData = Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSalesforceCredentialsRequest: BodyType<TLSalesforceCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSalesforceCrmCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateSalesforceCrmCredentialsQueryOptions(tLSalesforceCredentialsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Salesforce CRM Credentials
 */
export const prefetchCreateSalesforceCrmCredentialsQuery = async <
  TData = Awaited<ReturnType<typeof createSalesforceCrmCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLSalesforceCredentialsRequest: BodyType<TLSalesforceCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSalesforceCrmCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateSalesforceCrmCredentialsQueryOptions(tLSalesforceCredentialsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Salesforce CRM Credentials
 */
export const invalidateCreateSalesforceCrmCredentials = async (
  queryClient: QueryClient,
  tLSalesforceCredentialsRequest: BodyType<TLSalesforceCredentialsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateSalesforceCrmCredentialsQueryKey(tLSalesforceCredentialsRequest) },
    options,
  );

  return queryClient;
};

/**
 * Create new SAP ERP credentials for the user. Tests connectivity before saving.
 * @summary Create SAP ERP Credentials
 */
export const createSapErpCredentials = (
  tLSAPCredentialsRequest: BodyType<TLSAPCredentialsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSAPCredentialsResponse>(
    {
      url: `/user-credentials/sap`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSAPCredentialsRequest,
      signal,
    },
    options,
  );
};

export const getCreateSapErpCredentialsQueryKey = (tLSAPCredentialsRequest?: BodyType<TLSAPCredentialsRequest>) => {
  return ['POST', 'user-credentials', 'sap', tLSAPCredentialsRequest] as const;
};

export const getCreateSapErpCredentialsQueryOptions = <
  TData = Awaited<ReturnType<typeof createSapErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSAPCredentialsRequest: BodyType<TLSAPCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSapErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateSapErpCredentialsQueryKey(tLSAPCredentialsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createSapErpCredentials>>> = ({ signal }) =>
    createSapErpCredentials(tLSAPCredentialsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createSapErpCredentials>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateSapErpCredentialsQueryResult = NonNullable<Awaited<ReturnType<typeof createSapErpCredentials>>>;
export type CreateSapErpCredentialsQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateSapErpCredentials<
  TData = Awaited<ReturnType<typeof createSapErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSAPCredentialsRequest: BodyType<TLSAPCredentialsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSapErpCredentials>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createSapErpCredentials>>,
          TError,
          Awaited<ReturnType<typeof createSapErpCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateSapErpCredentials<
  TData = Awaited<ReturnType<typeof createSapErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSAPCredentialsRequest: BodyType<TLSAPCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSapErpCredentials>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createSapErpCredentials>>,
          TError,
          Awaited<ReturnType<typeof createSapErpCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateSapErpCredentials<
  TData = Awaited<ReturnType<typeof createSapErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSAPCredentialsRequest: BodyType<TLSAPCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSapErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create SAP ERP Credentials
 */

export function useCreateSapErpCredentials<
  TData = Awaited<ReturnType<typeof createSapErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSAPCredentialsRequest: BodyType<TLSAPCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSapErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateSapErpCredentialsQueryOptions(tLSAPCredentialsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create SAP ERP Credentials
 */
export const prefetchCreateSapErpCredentialsQuery = async <
  TData = Awaited<ReturnType<typeof createSapErpCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLSAPCredentialsRequest: BodyType<TLSAPCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSapErpCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateSapErpCredentialsQueryOptions(tLSAPCredentialsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create SAP ERP Credentials
 */
export const invalidateCreateSapErpCredentials = async (
  queryClient: QueryClient,
  tLSAPCredentialsRequest: BodyType<TLSAPCredentialsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateSapErpCredentialsQueryKey(tLSAPCredentialsRequest) },
    options,
  );

  return queryClient;
};
